import {
  Button,
  Center,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { setPageSizeMenu } from 'modules/Editor/store/pageControlSlice';
import { selectIsPageSizeMenuOpen } from 'modules/Editor/store/pageSelector';
import { InputError } from 'modules/common/components/Input';
import { ComponentProps, ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperType } from 'types/paper.types';

import { PageOrientation } from './PageOrientation';
import { PageHeightInput, PageWidthInput } from './PageSizeInput';
import { PapeSizeUnitSelect } from './PageSizeUnitSelect';
import { PaperTypeSelect } from './PaperTypeSelect';
import ScaleCheckbox from './ScaleCheckbox';
import { HD_EXPORT_BREAKPOINT, MAX_NUMBER_INPUT, MIN_NUMBER_INPUT, PAGE_SIZE_CONTENT_WIDTH } from './config';
import { format, isLongPaper } from './helpers';
import { usePageSizeControl } from './hooks';
import { useResizedToast } from './hooks/useResizedToast';

const Separator = ({ symbol = 'x', ...props }: { symbol?: string } & ComponentProps<typeof Center>) => {
  return (
    <Center fontSize='lg' {...props}>
      {symbol}
    </Center>
  );
};

export const EditPageSize = (): ReactElement => {
  const isMenuOpen = useAppSelector(selectIsPageSizeMenuOpen);
  const focusRef = useRef(null);
  const { t } = useTranslation('editor_toolbar_page_menu', { useSuspense: false });
  const dispatch = useAppDispatch();

  const onClose = () => dispatch(setPageSizeMenu(false));
  const onClick = () => {
    dispatch(setPageSizeMenu(!isMenuOpen));
  };

  // Reset the menu when component unmounts (i.e. widget selection changes)
  useEffect(() => {
    return () => {
      onClose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Popover
      initialFocusRef={focusRef}
      placement='bottom-start'
      isOpen={isMenuOpen}
      onClose={onClose}
      isLazy
      closeOnBlur
      closeOnEsc
    >
      <PopoverTrigger>
        <Button size='sm' variant='ghost' onClick={onClick} fontWeight='semibold'>
          {t('pageSize.editButton')}
        </Button>
      </PopoverTrigger>
      <EditPageSizeContent />
    </Popover>
  );
};

const EditPageSizeContent = () => {
  const { t } = useTranslation('editor_toolbar_page_menu', { useSuspense: false });

  const {
    widthPx,
    heightPx,
    paperType,
    orientation,
    unit,
    isScaled,
    isPageSizeExceeded,
    hasExceededHDExport,
    handleChangeScaled,
    handlePaperTypeChange,
    handleOrientationChange,
    handleUnitChange,
  } = usePageSizeControl();
  const { showToast } = useResizedToast();

  const handleChangePaperType = (newType: PaperType, idx: number) => {
    handlePaperTypeChange(newType, idx);
    showToast();
  };

  const inputErrorId = 'page-size-error';
  const inputError = t('pageSize.pageSizeExceedTitle', {
    maxPixel: format(MAX_NUMBER_INPUT, unit),
    minPixel: format(MIN_NUMBER_INPUT, unit),
    unit: unit,
  });

  const exportError = t('pageSize.exceedHdExportTitle', {
    maxPixel: format(HD_EXPORT_BREAKPOINT, unit),
    unit,
  });

  return (
    <PopoverContent bg='white' w={PAGE_SIZE_CONTENT_WIDTH} p='1' data-testid='page-menu-resize-content'>
      <PopoverHeader fontSize='sm' fontWeight='600' border='0'>
        {t('pageSize.title')}
      </PopoverHeader>
      <PopoverArrow />
      <PopoverBody>
        <Flex direction='column' gap='3'>
          <ScaleCheckbox
            isScaled={Boolean(isScaled)}
            disabled={isLongPaper({ widthPx, heightPx })}
            onChangeScaled={handleChangeScaled}
          />
          <PaperTypeSelect value={paperType} onChange={handleChangePaperType} />
          <Text fontSize='sm' fontWeight='600'>
            {t('pageSize.customTitle')}
          </Text>
          <Flex direction='row' gap='2'>
            <PageWidthInput value={widthPx} unit={unit} isScaled={isScaled} inputErrorId={inputErrorId} />
            <Separator mt='6' />
            <PageHeightInput value={heightPx} unit={unit} isScaled={isScaled} inputErrorId={inputErrorId} />
            <PapeSizeUnitSelect value={unit} onChange={handleUnitChange} />
          </Flex>

          {isPageSizeExceeded && <InputError id={inputErrorId}>{inputError}</InputError>}
          {hasExceededHDExport && !isPageSizeExceeded && <InputError id={inputErrorId}>{exportError}</InputError>}
          <PageOrientation orientation={orientation} onChange={handleOrientationChange} />
        </Flex>
      </PopoverBody>
    </PopoverContent>
  );
};
